import SeoHead from '../../components/feature/SeoHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Compra de Coches Siniestrados en Barcelona',
    description: 'Compramos coches siniestrados al mejor precio en Barcelona. Tasación inmediata, recogida gratis y pago al instante.',
    provider: {
      '@type': 'AutoDealer',
      name: 'Desguace Barcelona',
      telephone: '+34932932913',
      address: { '@type': 'PostalAddress', addressLocality: 'Barcelona', addressCountry: 'ES' },
    },
    areaServed: { '@type': 'AdministrativeArea', name: 'Provincia de Barcelona' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Puedo vender mi coche siniestrado si la aseguradora ya me ha indemnizado?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, en muchos casos puedes quedarte con el vehículo siniestrado y venderlo a un desguace. Nosotros compramos ese salvamento y en muchos casos ofrecemos más que el valor residual estimado por la aseguradora.' },
      },
      {
        '@type': 'Question',
        name: '¿Qué pasa si el coche siniestrado está en un depósito de grúa?',
        acceptedAnswer: { '@type': 'Answer', text: 'No hay problema. Podemos recoger el vehículo directamente del depósito sin coste adicional.' },
      },
      {
        '@type': 'Question',
        name: '¿Compran coches con daños estructurales graves?',
        acceptedAnswer: { '@type': 'Answer', text: 'Sí, compramos vehículos con cualquier tipo de daño, incluyendo daños estructurales, chasis doblado, airbags activados o motor dañado.' },
      },
    ],
  },
];

export default function VenderCocheSiniestroPage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Vender Coche Siniestrado en Barcelona | Mejor Precio | Desguace Barcelona"
        description="Compramos tu coche siniestrado al mejor precio en Barcelona. Tasación inmediata en 24h, recogida gratuita y pago al instante. Más de 15 años comprando coches accidentados."
        keywords="vender coche siniestrado Barcelona, coche siniestro total Barcelona, compra coches accidentados Barcelona, desguace coches siniestrados"
        canonical={`${siteUrl}/vender-coche-siniestro-barcelona`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a2332] to-[#2d3e50]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Vender Coche Siniestrado en Barcelona
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Compramos tu coche accidentado al mejor precio. Tasación inmediata y recogida gratis en toda Barcelona
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a 
                href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche%20siniestrado" 
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1fb855] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                WhatsApp
              </a>
              <a 
                href="tel:+34932932913" 
                className="inline-flex items-center justify-center px-10 py-5 bg-white text-[#1a2332] rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3"></i>
                932 932 913
              </a>
            </div>
          </div>
        </section>

        {/* Contenido Principal */}
        <article className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Introducción */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Si has sufrido un accidente de tráfico en Barcelona y tu vehículo ha sido declarado <strong>siniestro total</strong>, probablemente te estés preguntando qué hacer con el coche. La buena noticia es que en <strong>Desguace Barcelona</strong> compramos coches siniestrados al mejor precio del mercado, ofreciendo un servicio rápido, legal y sin complicaciones.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Con más de 15 años de experiencia en la compra de vehículos siniestrados en Barcelona y provincia, hemos ayudado a miles de personas a obtener el máximo valor por sus coches accidentados. Nuestro proceso es transparente y garantizamos que recibirás una oferta justa basada en el estado real del vehículo.
              </p>
            </div>

            {/* Sección 1 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                ¿Qué es un Coche Siniestrado?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Un <strong>coche siniestrado</strong> es aquel que ha sufrido daños en un accidente y cuyo coste de reparación supera un porcentaje determinado del valor del vehículo (generalmente entre el 60% y 80%). Cuando esto ocurre, la compañía de seguros declara el vehículo como <strong>siniestro total</strong> o <strong>pérdida total</strong>.
              </p>

              <div className="bg-[#f8f9fa] p-8 rounded-2xl mb-8">
                <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Tipos de Siniestros que Compramos</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332]">Siniestro Total Económico:</strong>
                      <p className="text-gray-700">El coste de reparación supera el valor del vehículo, pero el coche podría repararse técnicamente.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332]">Siniestro Total Técnico:</strong>
                      <p className="text-gray-700">Los daños son tan graves que el vehículo no puede repararse de forma segura o legal.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332]">Daños Parciales Graves:</strong>
                      <p className="text-gray-700">Vehículos con daños importantes que no han sido declarados siniestro total pero que el propietario prefiere vender.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#ff6b35] text-2xl flex-shrink-0 mt-1"></i>
                    <div>
                      <strong className="text-[#1a2332]">Vehículos Quemados:</strong>
                      <p className="text-gray-700">Coches que han sufrido incendios, ya sea por accidente o por causas técnicas.</p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed">
                En todos estos casos, <strong>Desguace Barcelona</strong> puede ofrecerte una solución rápida y rentable. Evaluamos cada vehículo individualmente para determinar el valor de las piezas recuperables y te hacemos una oferta competitiva.
              </p>
            </section>

            {/* Sección 2 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                ¿Por Qué Vender tu Coche Siniestrado a Desguace Barcelona?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="w-14 h-14 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-money-euro-circle-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Mejor Precio Garantizado</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Ofrecemos precios superiores a la mayoría de aseguradoras y otros desguaces. Nuestro equipo de expertos valora correctamente cada pieza recuperable para maximizar tu beneficio.
                  </p>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="w-14 h-14 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-time-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Proceso Ultra Rápido</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Tasación en menos de 24 horas y recogida en 48-72 horas. No tendrás que esperar semanas como con algunos seguros o gestorías.
                  </p>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="w-14 h-14 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-truck-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Recogida Gratuita</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Recogemos tu coche siniestrado donde esté: en tu garaje, en un taller, en la vía pública o en el depósito de la grúa. Sin coste adicional en toda Barcelona y provincia.
                  </p>
                </div>

                <div className="bg-white border-2 border-[#1a2332] p-6 rounded-xl">
                  <div className="w-14 h-14 bg-[#ff6b35] rounded-full flex items-center justify-center mb-4">
                    <i className="ri-file-text-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-3">Gestión Legal Completa</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Nos encargamos de todos los trámites: certificado de destrucción, baja en Tráfico, notificación a la aseguradora y cualquier otro papeleo necesario.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 3 */}
            <section className="mb-16 bg-gradient-to-br from-[#f8f9fa] to-[#e8f0f7] p-8 rounded-2xl">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Proceso para Vender tu Coche Siniestrado
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Contacta con Nosotros</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Llámanos o rellena el formulario online. Necesitamos información básica: matrícula, marca, modelo, año y descripción de los daños. Si tienes fotos del vehículo, mejor aún.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Evaluación y Oferta</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Nuestros expertos evalúan el vehículo basándose en la información proporcionada. En menos de 24 horas recibirás una oferta detallada y sin compromiso. Explicamos claramente cómo llegamos al precio.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Inspección in Situ (Opcional)</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Si lo prefieres, podemos enviar a un técnico para inspeccionar el vehículo personalmente antes de confirmar la oferta final. Este servicio es gratuito en Barcelona y alrededores.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Recogida y Pago</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Acordamos día y hora para recoger el vehículo. Nuestro equipo llega con toda la documentación necesaria. Verificamos el estado, firmamos los papeles y realizamos el pago inmediato: transferencia o efectivo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a2332] text-white rounded-full flex items-center justify-center font-black text-lg">
                      5
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-[#1a2332] mb-2">Trámites Legales</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Nos encargamos de tramitar el certificado de destrucción y la baja definitiva en la DGT. Recibirás toda la documentación que acredita que el vehículo ha sido dado de baja legalmente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Sección 4 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Preguntas Frecuentes sobre Coches Siniestrados
              </h2>

              <div className="space-y-4">
                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Puedo vender mi coche siniestrado si la aseguradora ya me ha indemnizado?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Sí, en muchos casos puedes quedarte con el vehículo siniestrado y venderlo a un desguace. La aseguradora te indemniza por el valor del coche menos el valor residual (salvamento). Nosotros compramos ese salvamento, y en muchos casos ofrecemos más que el valor residual estimado por la aseguradora.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Qué pasa si el coche siniestrado está en un depósito de grúa?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    No hay problema. Podemos recoger el vehículo directamente del depósito. En algunos casos, incluso podemos ayudarte a negociar los costes de grúa y depósito para que no tengas que pagarlos de tu bolsillo.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Compran coches con daños estructurales graves?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Sí, compramos vehículos con cualquier tipo de daño, incluyendo daños estructurales, chasis doblado, airbags activados, motor dañado, etc. Incluso si el coche parece no tener valor, muchas piezas pueden ser recuperables.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Cuánto tiempo tarda todo el proceso?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    Desde que nos contactas hasta que recibes el pago, el proceso completo suele tardar entre 3 y 5 días laborables. La tasación la hacemos en menos de 24 horas, y la recogida se programa según tu disponibilidad.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-[#ff6b35] p-6 rounded-r-xl shadow-sm">
                  <h4 className="text-lg font-bold text-[#1a2332] mb-2">
                    ¿Necesito llevar el coche a vuestras instalaciones?
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    No, nunca. Nosotros vamos a recoger el vehículo donde esté: tu casa, garaje, taller, depósito de grúa o cualquier otro lugar en Barcelona y provincia. El servicio de recogida es completamente gratuito.
                  </p>
                </div>
              </div>
            </section>

            {/* Sección 5 */}
            <section className="mb-16">
              <h2 className="text-3xl font-black text-[#1a2332] mb-6">
                Zonas de Barcelona Donde Compramos Coches Siniestrados
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Operamos en toda Barcelona y provincia con servicio de recogida gratuita:
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Barcelona Ciudad', 'Badalona', 'L\'Hospitalet', 'Sabadell', 'Terrassa', 'Mataró', 'Granollers', 'Vilafranca', 'Sant Cugat', 'Cornellà', 'Rubí', 'Manresa', 'Vilanova', 'Gavà', 'Castelldefels', 'Provincia'].map((zona, index) => (
                  <div key={index} className="bg-[#f8f9fa] p-3 rounded-lg text-center">
                    <i className="ri-map-pin-line text-[#ff6b35] mr-1"></i>
                    <span className="text-sm font-medium text-[#1a2332]">{zona}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Final */}
            <section className="bg-gradient-to-br from-[#1a2332] to-[#2d3e50] rounded-2xl p-12 text-center text-white">
              <h2 className="text-4xl font-black mb-4">
                Vende tu Coche Siniestrado Hoy
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Mejor precio garantizado • Recogida gratis • Pago inmediato
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://wa.me/34932932913?text=Hola%20Desguaces%20Barcelona%2C%20quiero%20valorar%20mi%20coche%20siniestrado" 
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center justify-center px-10 py-5 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1fb855] transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-3 text-2xl"></i>
                  Solicitar Tasación
                </a>
                <a 
                  href="tel:+34932932913" 
                  className="inline-flex items-center justify-center px-10 py-5 bg-white text-[#1a2332] rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-3"></i>
                  932 932 913
                </a>
              </div>
            </section>

          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
